import React, { useState } from 'react';
import { plata, peso, fecha, hoy } from '../utils/formato';
import { enPesos } from '../utils/letras';
import Membrete from './Membrete';
import { diasDelPeriodo, cuentaDeLaRelacion,
         cambiosDeEstaRelacion } from '../utils/reporte';
import { usePapel, llaveDeRelacion } from '../utils/consecutivo';

// Los datos de la empresa salen de la sesión guardada, igual que en los otros
// dos papeles. Pasarlos a mano por cada pantalla que imprime es una cadena que
// se rompe en la que se olvide.
function delNegocio() {
  try {
    const s = JSON.parse(localStorage.getItem('quesera-sesion'));
    const u = s && s.user;
    return { negocio: u?.negocio || null, nit: u?.nit || null,
             direccion: u?.direccion || null, ciudad: u?.ciudad || null,
             telefono: u?.telefono || null, logo: u?.logo || null };
  } catch { return {}; }
}
import { moverPeriodo, nombreDelPeriodo, FRECUENCIAS } from '../utils/periodo';

// La relación de leche que se le entrega a la finca.
//
// Es la hoja que llevaban en Excel: un renglón por día del calendario, las dos
// leches en columnas aparte, y al pie el descuento de los préstamos.
//
// Va aparte del comprobante de egreso a propósito. Son dos papeles distintos:
// esta es el DETALLE —lo que el finquero revisa con el dedo antes de cobrar— y
// el comprobante es el RECIBO, que se firma al recibir la plata.

function Relacion({ r, alCerrar, alCambiarPeriodo }) {
  // ANTES del `return null`: React exige que los enganches se llamen en el
  // mismo orden en cada pintada, y con el papel cerrado este se saltaría.
  // Solo la hoja que TIENE leche. Pasando semanas con las flechas, numerar las
  // vacías haría crecer la serie de solo mirar.
  const hayQueNumerar = !!(r && (r.recibos || []).length > 0);
  const { numero, nota, guardarNota } = usePapel('REL',
    hayQueNumerar
      ? llaveDeRelacion('f', r.finca && r.finca.id, r.desde, r.hasta) : null,
    r && r.hasta);
  // El recuadro de escribir la nota. Cerrado mientras no se oprima el botón:
  // quien no quiera poner nada no tiene que ver ni cerrar nada.
  const [escribiendo, setEscribiendo] = useState(null);
  if (!r) return null;
  const frec = r.finca.frecuencia_pago || 'mensual';
  // El día en que empieza la semana de ESTA finca. La de este cliente va de
  // sábado a viernes, no de lunes a domingo.
  const empieza = r.finca.semana_empieza;
  // Y qué día empieza su QUINCENA, que tampoco es siempre el 1: hay acuerdos
  // que cortan el 5 y el 20. Sin esto, las flechas movían a esa finca a la
  // quincena del calendario y no a la suya.
  const quincena = r.finca.quincena_empieza;
  const arranque = r.finca.ciclo_desde;

  const dias = diasDelPeriodo(r.recibos, r.desde, r.hasta);
  // Con el periodo: la hoja de una semana ya liquidada tiene que decir lo que
  // se le descontó ESE día, no el saldo de hoy.
  const c = cuentaDeLaRelacion(dias, r.prestamos, r.abonos,
                               { desde: r.desde, hasta: r.hasta });
  const hayBufala = c.litrosBufala > 0;
  // Los cambios de precio que pisan esta semana. Es lo que explica que la
  // leche valga distinto de lo que se le descontó.
  const cambios = cambiosDeEstaRelacion(r.cambios, r.finca,
                                        { desde: r.desde, hasta: r.hasta });

  return (
    <div className="capa-imprimir">
      <div className="barra-imprimir">
        {/* Moverse de periodo sin salir de la hoja. Se abre en el que corre
            ahora, pero casi siempre se liquida el que acaba de cerrar — y
            corregir dos fechas a mano cada vez es donde se cuela el error. */}
        <button className="boton suave"
                onClick={() => alCambiarPeriodo(
                  moverPeriodo({ desde: r.desde, hasta: r.hasta }, frec, -1, empieza, quincena, arranque))}>
          ◀ Anterior
        </button>
        <button className="boton suave"
                onClick={() => alCambiarPeriodo(
                  moverPeriodo({ desde: r.desde, hasta: r.hasta }, frec, 1, empieza, quincena, arranque))}>
          Siguiente ▶
        </button>
        <div style={{ flex: 1 }} />
        {/* LA NOTA DE ESTE PAPEL.

            El botón dice si ya hay una puesta. Sin eso, una nota escrita hace
            tres semanas sigue saliendo impresa y nada en la pantalla lo
            recuerda — y quien la lee es el finquero, no Gabriel.

            Solo cuando el papel está numerado: sin fila en `documentos` no hay
            a qué colgarle la nota, y una semana sin leche no se numera. */}
        {hayQueNumerar && (
          <button className="boton suave"
                  onClick={() => setEscribiendo(nota || '')}
                  title={nota ? 'Este papel lleva una nota' : 'Escribirle una nota a este papel'}>
            {nota ? '📝 Nota puesta' : '📝 Nota'}
          </button>
        )}
        <button className="boton suave" onClick={alCerrar}>Cerrar</button>
        <button className="boton" onClick={() => window.print()}>🖨️ Imprimir</button>
      </div>

      {/* El recuadro de escribir va por `solo-pantalla`: lo que se imprime es
          la nota dentro de la hoja, no la caja donde se teclea. */}
      {escribiendo !== null && (
        <div className="solo-pantalla"
             style={{ maxWidth: 820, margin: '0 auto 12px', padding: 14,
                      borderRadius: 8, border: '1px solid #bbb',
                      background: '#FFFDF3' }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            Nota para esta relación
          </div>
          <div style={{ fontSize: 12, color: '#555', marginBottom: 8 }}>
            Sale impresa en <strong>este papel</strong> y en ningún otro.
            Para quitarla, borre el texto y guarde.
          </div>
          <textarea
            value={escribiendo}
            autoFocus
            rows={4}
            maxLength={600}
            placeholder={'Estimado cliente, a partir del 1 de marzo, debido a los ' +
                         'bajos precios del mercado del queso, estaremos regulando ' +
                         'precios de leche.'}
            onChange={e => setEscribiendo(e.target.value)}
            style={{ width: '100%', padding: 9, borderRadius: 6,
                     border: '1px solid #bbb', fontSize: 14,
                     fontFamily: 'inherit', resize: 'vertical' }} />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                        marginTop: 8, flexWrap: 'wrap' }}>
            <button className="boton"
                    onClick={() => guardarNota(escribiendo)
                      .then(() => setEscribiendo(null))
                      .catch(err => alert(
                        (err.response && err.response.data && err.response.data.error) ||
                        'No se pudo guardar la nota.'))}>
              Guardar
            </button>
            <button className="boton suave" onClick={() => setEscribiendo(null)}>
              Cancelar
            </button>
            <span style={{ fontSize: 12, color: '#777', marginLeft: 'auto' }}>
              {escribiendo.length} de 600
            </span>
          </div>
        </div>
      )}

      <div className="hoja">
        <div className="comprobante">
          <Membrete
            v={{ ...delNegocio(), numero }}
            titulo="RELACIÓN DE COMPRA DE LECHE"
            cual={'Emitida el ' + fecha(hoy())} />

          <table className="cuerpo-c" style={{ marginBottom: 14 }}>
            <tbody>
              <tr>
                <td className="rot">Proveedor</td>
                <td className="neg">{r.finca.propietario || r.finca.nombre}</td>
                <td className="rot">C.C. o NIT</td>
                <td>{r.finca.documento || '—'}</td>
              </tr>
              <tr>
                <td className="rot">Finca</td>
                <td>{r.finca.nombre}</td>
                <td className="rot">Periodo</td>
                <td>
                  {nombreDelPeriodo({ desde: r.desde, hasta: r.hasta }, frec, quincena)}:{' '}
                  {fecha(r.desde)} al {fecha(r.hasta)}
                  <div className="chico">
                    Se le paga {(FRECUENCIAS[frec] || 'Mensual').toLowerCase()}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <table className="relacion">
            <thead>
              <tr>
                {/* Las columnas son las de la factura que ellos ya llevan a
                    mano: DÍA · DESCRIPCIÓN · CANT · VALOR · TOTAL. El VALOR es
                    el precio del litro y el TOTAL lo que se pagó ese día.
                    Faltaba el precio, y sin él quien revisa no puede comprobar
                    la multiplicación — que es lo primero que hace. */}
                <th>Día</th>
                <th>Descripción</th>
                <th className="der">Cant.{hayBufala ? ' vaca' : ''}</th>
                <th className="der">Valor</th>
                <th className="der">Total</th>
                {hayBufala && <th className="der">Cant. búfala</th>}
                {hayBufala && <th className="der">Valor</th>}
                {hayBufala && <th className="der">Total</th>}
              </tr>
            </thead>
            <tbody>
              {dias.map(d => {
                const vacio = d.litrosVaca === 0 && d.litrosBufala === 0;
                return (
                  <tr key={d.fecha} className={vacio ? 'vacio' : ''}>
                    <td>{fecha(d.fecha)}</td>
                    <td>Litros de leche</td>
                    {/* El cero se escribe. Una celda en blanco se lee como
                        «falta el dato»; un 0 dice «ese día no entregó», que es
                        una afirmación y no un olvido. */}
                    <td className="der">{d.litrosVaca || 0}</td>
                    <td className="der">{d.precioVaca ? plata(d.precioVaca) : '—'}</td>
                    <td className="der">{d.valorVaca ? plata(d.valorVaca) : '—'}</td>
                    {hayBufala && <td className="der">{d.litrosBufala || 0}</td>}
                    {hayBufala && (
                      <td className="der">{d.precioBufala ? plata(d.precioBufala) : '—'}</td>
                    )}
                    {hayBufala && (
                      <td className="der">{d.valorBufala ? plata(d.valorBufala) : '—'}</td>
                    )}
                  </tr>
                );
              })}
              <tr className="subt">
                <td colSpan={2}>SUBTOTALES</td>
                <td className="der">{c.litrosVaca}</td>
                <td></td>
                <td className="der">{plata(c.valorVaca)}</td>
                {hayBufala && <td className="der">{c.litrosBufala}</td>}
                {hayBufala && <td></td>}
                {hayBufala && <td className="der">{plata(c.valorBufala)}</td>}
              </tr>
            </tbody>
          </table>

          <table className="detalle" style={{ marginTop: 14 }}>
            <tbody>
              <tr>
                <td>Total leche entregada — {peso(c.litrosVaca + c.litrosBufala, 'L')}</td>
                <td className="der">{plata(c.total)}</td>
              </tr>
              {/* EL PRÉSTAMO, CON LO QUE YA ABONÓ Y LO QUE LE QUEDA.
                  
                  «Cuando se le hace el préstamo al de la finca que salga
                  también en la factura, y si va abonando que salga el detalle
                  de cuánto abonó y cuánto resta.»
                  
                  Y de paso arregla un cobro doble: se descontaba `monto` —el
                  préstamo entero— aunque llevara medio pagado. Ahora se
                  descuenta el SALDO, y los tres renglones dejan comprobar la
                  resta con el dedo, que es lo que él revisa antes de firmar. */}
              {(c.prestamos || []).map((p, n) => (
                <React.Fragment key={n}>
                  {/* EL PRÉSTAMO QUE NO SE DESCUENTA TAMBIÉN SE NOMBRA.

                      Sin cuota pactada el programa ya no lo descuenta solo
                      —nadie lo autorizó— pero callarlo sería peor: el finquero
                      tiene que saber que sigue ahí, y Gabriel tiene que verlo
                      al ir a pagar. Se dice, y se dice que NO se está
                      descontando, que es la mitad que importa. */}
                  {p.saldo <= 0 ? (
                    <tr className="vacio">
                      <td>
                        Tiene un préstamo del {fecha(p.fecha)}
                        {p.nota ? ` — ${p.nota}` : ''}
                        <div className="chico">
                          {/* El «de $X» solo cuando ya abonó algo. Sin abonos,
                              «le quedan $3.000.000 de $3.000.000» dice dos
                              veces la misma cifra y hace dudar de si son la
                              misma. */}
                          Le quedan {plata(p.queda)}
                          {p.queda !== Math.round(p.monto)
                            ? <> de {plata(p.monto)}</> : null}.
                          {' '}Esta vez no se le descuenta.
                        </div>
                      </td>
                      <td className="der">—</td>
                    </tr>
                  ) : (
                  <tr>
                    <td>
                      Menos préstamo del {fecha(p.fecha)}
                      {p.nota ? ` — ${p.nota}` : ''}
                      <div className="chico">
                        Se le prestaron {plata(p.monto)}
                        {p.traia !== p.monto && (
                          <> · traía {plata(p.traia)} al empezar la semana</>
                        )}
                      </div>
                    </td>
                    <td className="der">−{plata(p.saldo)}</td>
                  </tr>
                  )}
                  {/* SOLO LOS ABONOS DE ESTE PERIODO, con su fecha.
                      
                      Antes salían todos los del préstamo, así que la hoja del
                      29 de agosto enseñaba un abono del 11 de septiembre. Esta
                      es la hoja que el finquero guarda y con la que vuelve a
                      reclamar tres meses después: un día de más se lee como
                      plata cobrada dos veces. */}
                  {p.abonos.map((a, k) => (
                    <tr key={k} className="vacio">
                      <td style={{ paddingLeft: 22 }}>
                        {/* Sin nota: los abonos a préstamos no la llevan.
                            Ponerla dejaría un hueco que parece un dato que
                            falta. */}
                        Abonó el {fecha(a.fecha)}
                      </td>
                      <td className="der">{plata(a.monto)}</td>
                    </tr>
                  ))}
                  {/* Solo cuando de verdad se descontó algo. Sin descuento, el
                      renglón de arriba ya dijo cuánto le queda, y repetirlo
                      como «después de este descuento» nombraría un descuento
                      que no existe. */}
                  {p.saldo > 0 && (
                    <tr className="vacio">
                      <td style={{ paddingLeft: 22 }}>
                        {/* «Del préstamo le quedan» se leía como el saldo de
                            hoy, al lado de un renglón que acababa de
                            descontarlo entero. Es DESPUÉS del descuento de esta
                            hoja, y así hay que decirlo. No se nombra la semana
                            porque a PABLO POLO se le paga quincenal. */}
                        Después de este descuento le quedan
                      </td>
                      <td className="der">{plata(p.queda)}</td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              <tr className="neto">
                <td>TOTAL A PAGAR</td>
                <td className="der">{plata(c.aPagar)}</td>
              </tr>
            </tbody>
          </table>

          {/* DE DÓNDE SALE LO QUE SE ABONÓ DE MÁS.
              
              «Los abonos no deben cambiar por el alza o baja, para que el
              cliente no piense que se le está robando.»
              
              Cuando a una semana ya pagada se le sube el precio, esa semana
              queda debiéndole al finquero y se le abona en el corte siguiente.
              Entonces el abono de la semana siguiente sale MAYOR que su leche
              —$731.100 contra $716.000— y sin este renglón no hay de dónde
              sacar la diferencia. Leído por quien cobra, es plata que apareció
              de la nada. */}
          {c.deAntes > 0 && (
            <p style={{ fontSize: 12, marginTop: 8 }}>
              De lo abonado, <strong>{plata(c.deAntes)}</strong> no son de esta
              semana: es lo que quedó pendiente de semanas anteriores, cuando se
              les ajustó el precio. De <em>esta</em> semana se abonaron{' '}
              <strong>{plata(c.deEsta)}</strong>, que es justo lo que valió su
              leche.
            </p>
          )}

          {/* ── POR QUÉ CAMBIÓ EL PRECIO ────────────────────────────────
              
              «Dejarle en la factura el detalle claro, que el dueño de la finca
              sepa por qué la variación de precios, para que estén tranquilos.»
              
              A NIÑO BERMÚDEZ le llegaron dos relaciones donde los abonos no
              cuadraban con la leche de cada semana, y nadie podía explicar de
              dónde salían $15.100. Estaban en un cambio de precio hecho DESPUÉS
              de pagarle esa semana: la leche pasó a valer más y la diferencia
              se le abonó en el pago siguiente.
              
              Todo estaba bien y aun así pareció un error, porque el papel no lo
              decía. Un papel que no explica una diferencia la convierte en una
              discusión. */}
          {/* EN PANTALLA SÍ, EN EL PAPEL NO.

              «Esto es informativo para Gabriel pero no debe ir en la impresión.»

              A él le sirve: le dice por qué la cifra de esa semana se movió. Al
              finquero que firma no — es cocina interna de la quesera, y en un
              papel de cobro una explicación de más abre una discusión de más.

              Va por `solo-pantalla`, que el @media print oculta. */}
          {cambios.length > 0 && (
            <div className="solo-pantalla"
                 style={{ marginTop: 14, padding: '10px 14px', borderRadius: 8,
                          border: '1px solid #bbb', background: '#FFF6E0',
                          fontSize: 12 }}>
              <strong>
                {cambios.length === 1
                  ? 'El precio de esta semana se ajustó'
                  : 'El precio de esta semana se ajustó ' + cambios.length + ' veces'}
              </strong>
              {cambios.map((x, n) => (
                <div key={n} style={{ marginTop: 6 }}>
                  El {fecha(x.cuando)} se puso el litro
                  {x.tipo_leche === 'bufala' ? ' de búfala' : ''} a{' '}
                  <strong>{plata(x.precio_nuevo)}</strong> para los días del{' '}
                  {fecha(x.desde)} al {fecha(x.hasta)}.{' '}
                  {x.diferencia > 0
                    ? <>Su leche de esos días pasó de {plata(x.total_antes)} a{' '}
                       <strong>{plata(x.total_despues)}</strong>: {plata(x.diferencia)} más.</>
                    : <>Su leche de esos días pasó de {plata(x.total_antes)} a{' '}
                       <strong>{plata(x.total_despues)}</strong>: {plata(-x.diferencia)} menos.</>}
                  {x.nota ? ` (${x.nota})` : ''}
                </div>
              ))}
              {/* La frase que contesta la pregunta de verdad: «¿y esa
                  diferencia dónde está?». Si la semana ya se había liquidado,
                  no se rehace el pago —esa plata ya cambió de manos— sino que
                  se ajusta en el siguiente. */}
              <div style={{ marginTop: 6 }}>
                Si esta semana ya se le había pagado cuando se hizo el ajuste,
                la diferencia <strong>no se pierde</strong>: se le abona en el
                pago siguiente.
              </div>
            </div>
          )}

          {/* Si el préstamo se comió toda la leche, se dice. Un «total a pagar
              $0» sin explicación parece un error del programa. */}
          {c.quedaDebiendo > 0 && (
            <p style={{ fontSize: 12, marginTop: 8 }}>
              Los préstamos superan la leche de este periodo. Quedan debiendo{' '}
              <strong>{plata(c.quedaDebiendo)}</strong>, que se descuentan del
              próximo pago.
            </p>
          )}

          <p style={{ fontSize: 12, marginTop: 10 }}>
            <strong>Son: {enPesos(c.aPagar)}</strong>
          </p>

          {/* LA NOTA, Y ÉSTA SÍ SE IMPRIME.

              Es lo contrario del aviso del cambio de precio de más arriba: aquel
              es cocina interna y se queda en pantalla; ésta se escribe A
              PROPÓSITO para que el finquero la lea.

              Va aquí, entre el total y las firmas, que es donde Gabriel la tenía
              en el Excel. Arriba del total distraería de la cifra; debajo de las
              firmas es donde nadie mira.

              Sin marco de color: en el Excel es amarilla, pero la mayoría de las
              impresoras traen los fondos apagados y saldría un recuadro vacío.
              Un borde y la palabra NOTA en negrilla se leen igual en cualquiera.

              `white-space: pre-wrap` conserva los renglones tal como se
              escribieron: quien separa dos avisos con una línea en blanco espera
              verlos separados en el papel. */}
          {nota && (
            <div style={{ marginTop: 14, border: '1px solid #999', borderRadius: 4,
                          display: 'flex', fontSize: 12 }}>
              <div style={{ padding: '8px 10px', fontWeight: 700,
                            borderRight: '1px solid #999', alignSelf: 'stretch',
                            display: 'flex', alignItems: 'center' }}>
                NOTA:
              </div>
              <div style={{ padding: '8px 10px', whiteSpace: 'pre-wrap', flex: 1 }}>
                {nota}
              </div>
            </div>
          )}

          <div className="firmas">
            <div>
              <div className="linea" />
              <div className="chico">Elaborado por</div>
            </div>
            <div>
              <div className="linea" />
              <div className="chico">Recibí conforme — {r.finca.propietario || r.finca.nombre}</div>
              <div className="chico">C.C. {r.finca.documento || ''}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Relacion;
